import type {Locale} from '@commercial-website/data-layer/locale'
import {locales as defaultLocales} from '@commercial-website/data-layer/locale'
import {
    LegacyLocaleSwitcher as UILegacyLocaleSwitcher,
    LocaleSwitcher as UIRebrandLocaleSwitcher,
    rebrandFlags,
} from '@commercial-website/ui'
import Cookies from 'js-cookie'
import React from 'react'

import {TrackedLocalizedLink} from '@/components/localized-link'
import {useLocale} from '@/providers/locale'
import {countryFlags, rebrandCountryFlags} from '@/utils/country-flags'

import {cookiesMap} from '@helios/website/src/config/cookies'
import {useGlobalStrings} from '@helios/website/src/providers/global-strings'
import {usePageInfo} from '@helios/website/src/providers/page-info'

const PLEO_GLOBAL_PAGE_URL = 'https://www.pleo.io/en/global'

interface LocaleSwitcherProps {
    light?: boolean
    locales?: Locale[]
}

type UILegacyLocaleSwitcherProps = React.ComponentProps<typeof UILegacyLocaleSwitcher>
type UIRebrandLocaleSwitcherProps = React.ComponentProps<typeof UIRebrandLocaleSwitcher>
type LocaleRender = NonNullable<UIRebrandLocaleSwitcherProps['locales'][number]['render']>

type GlobalLinkProps = {
    label: string
    children: React.ReactNode
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

const GlobalLink = React.forwardRef<HTMLAnchorElement, GlobalLinkProps>(
    ({label, children, ...props}, ref) => {
        return process.env.ENVIRONMENT === 'website' ? (
            <TrackedLocalizedLink
                ref={ref}
                trackingData={{
                    linkText: label,
                    linkType: 'element',
                    elementLocation: 'locale switcher',
                }}
                rawPath
                to={`/en/global`}
                {...props}
            >
                {children}
            </TrackedLocalizedLink>
        ) : (
            <a ref={ref} href={PLEO_GLOBAL_PAGE_URL} {...props}>
                {children}
            </a>
        )
    },
)

// Shared per-locale render: a tracked link that lets the Select drive navigation via
// `onLocaleChange` (the link's default navigation is prevented). Used by both variants.
const renderLocaleLink =
    (locale: Locale): LocaleRender =>
    ({children, onClick, ...props}) => (
        <TrackedLocalizedLink
            trackingData={{
                linkText: locale.countryName,
                linkType: 'element',
                elementLocation: 'locale switcher',
            }}
            rawPath
            to={`/${locale.urlCode}`}
            {...props}
            onClick={(e) => {
                e.preventDefault()
                onClick?.(e)
            }}
        >
            {children}
        </TrackedLocalizedLink>
    )

const mapLegacyLocales = (
    locales: Locale[],
    globalLabel: string,
): UILegacyLocaleSwitcherProps['locales'] => {
    const mappedLocales: UILegacyLocaleSwitcherProps['locales'] = locales.map((locale) => ({
        code: locale.code,
        countryName: locale.countryName,
        languageName: locale.languageName,
        flag: countryFlags[locale.country],
        render: renderLocaleLink(locale),
    }))
    mappedLocales.push({
        code: 'global',
        label: globalLabel,
        render: ({children, onClick, ...props}) => (
            <GlobalLink label={globalLabel} {...props} onClick={(e) => onClick?.(e)}>
                {children}
            </GlobalLink>
        ),
    })

    return mappedLocales
}

// The rebrand switcher intentionally has no "Global" entry and uses the rebrand (circular) flag
// set; navigation and tracking behaviour is otherwise identical to the legacy variant.
const mapRebrandLocales = (locales: Locale[]): UIRebrandLocaleSwitcherProps['locales'] =>
    locales.map((locale) => ({
        code: locale.code,
        countryName: locale.countryName,
        languageName: locale.languageName,
        flag: rebrandCountryFlags[locale.country],
        render: renderLocaleLink(locale),
    }))

const LocaleSwitcher = ({light, locales: customLocales}: LocaleSwitcherProps) => {
    const currentLocale = useLocale()
    const {pageContext} = usePageInfo()
    const globalStrings = useGlobalStrings()
    const locales = customLocales ?? defaultLocales

    /*
     * Using window.location.assign instead of navigate to force a full page reload.
     * This ensures that the correct experiment cookies are evaluated for the new locale,
     * preventing the user from remaining stuck in a variant specific to their previous locale.
     */
    const handleLocaleSwitch = (localeCode: string) => {
        const search = window.location.search
        if (localeCode === 'global') {
            if (process.env.ENVIRONMENT === 'website') {
                window.location.assign(`/en/global${search}`)
            } else {
                window.location.assign(`${PLEO_GLOBAL_PAGE_URL}${search}`)
            }
            return
        }

        const targetLocale = locales.find((locale) => locale.code === localeCode) ?? locales[0]
        const destination = pageContext.paths?.[targetLocale.code] ?? `/${targetLocale.urlCode}`

        Cookies.set(cookiesMap.defaultCountry, targetLocale.urlCode)

        window.location.assign(`${destination}${search}`)
    }

    return rebrandFlags.isRebrandFooter ? (
        <UIRebrandLocaleSwitcher
            locales={mapRebrandLocales(locales)}
            currentLocaleCode={currentLocale.code}
            onLocaleChange={handleLocaleSwitch}
        />
    ) : (
        <UILegacyLocaleSwitcher
            locales={mapLegacyLocales(locales, globalStrings.localeSelectorGlobalLabel)}
            currentLocaleCode={currentLocale.code}
            onLocaleChange={handleLocaleSwitch}
            compact={!light}
        />
    )
}

export default LocaleSwitcher
